import React, { useCallback, useState, ReactNode } from 'react';
import './ConfirmModal.css';

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  /** Styles the confirm button red for destructive actions */
  danger?: boolean;
}

interface ConfirmModalProps extends ConfirmOptions {
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  title, message, confirmText, cancelText, danger, onConfirm, onCancel,
}) => (
  // stopPropagation keeps clicks from bubbling into a parent modal's close handler
  <div className="confirm-modal-overlay" onClick={(e) => { e.stopPropagation(); onCancel(); }}>
    <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
      <h3>{title || 'Are you sure?'}</h3>
      <p className="confirm-modal-message">{message}</p>
      <div className="confirm-modal-actions">
        <button className="confirm-modal-btn confirm-modal-cancel" onClick={onCancel} autoFocus>
          {cancelText || 'Cancel'}
        </button>
        <button
          className={`confirm-modal-btn ${danger ? 'confirm-modal-danger' : 'confirm-modal-confirm'}`}
          onClick={onConfirm}
        >
          {confirmText || 'Confirm'}
        </button>
      </div>
    </div>
  </div>
);

/**
 * Promise-based replacement for window.confirm.
 *
 * const { confirm, confirmDialog } = useConfirm();
 * ...render {confirmDialog} once in the component tree...
 * if (await confirm({ message: 'Delete this?' , danger: true })) { ... }
 */
export function useConfirm(): {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  confirmDialog: ReactNode;
} {
  const [pending, setPending] = useState<{
    options: ConfirmOptions;
    resolve: (result: boolean) => void;
  } | null>(null);

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => setPending({ options, resolve })),
    []
  );

  const close = (result: boolean) => {
    pending?.resolve(result);
    setPending(null);
  };

  const confirmDialog = pending ? (
    <ConfirmModal
      {...pending.options}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  ) : null;

  return { confirm, confirmDialog };
}
